import React, { TextareaHTMLAttributes } from 'react'
import cn from 'classnames'
import styled from 'styled-components'
import { $secondaryWithAlpha, colors } from '../../styleGuide'

export interface TextareaInputProps
  extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  className?: string
  label?: string
  name: string
  error?: string
  variant?: 'normal' | 'solid' | 'outline' | 'line'
  dimension?: 'small' | 'medium' | 'big'
  onChange?: any
  value?: any
  disabled?: boolean
  placeholder?: string
  isShowError?: boolean
  errorMessage?: string
}

const StyledTextarea = styled.textarea<{
  variant: string
  dimension: string
  disabled?: boolean
}>`
  resize: none;
  height: 150px;
  min-width: 100%;
  outline: none;
  border-radius: 0.375rem;
  padding: 10px;
  font-size: 14px;
  color: #333;
  background-color: #fff;
  transition: all 0.3s ease-in-out;

  ${({ variant }) => {
    switch (variant) {
      case 'normal':
        return `
          background-color: ${colors.gray100};
          border: 1px solid ${colors.grayInputBorder};
          &:focus {
            border-color: ${colors.grayInputBorder};
            box-shadow: rgb(95, 56, 249) 0px 0px 0px 1px;
          }
        `
      case 'solid':
        return `
          background-color: ${colors.gray100};
          border: 1px solid ${colors.grayInputBorder};
          &:focus {
            border-color: ${colors.grayInputBorder};
            box-shadow: 0 0 0 0.25rem ${$secondaryWithAlpha('0.15')};
          }
        `
      case 'outline':
        return `
          background-color: transparent;
          border: 1px solid ${colors.grayInputBorder};
          &:focus {
            border-color: ${colors.accent};
            box-shadow: 0 0 0 0.25rem ${$secondaryWithAlpha('0.15')};
          }
        `
      case 'line':
        return `
          background-color: transparent;
          border: none;
          border-bottom: 1px solid ${colors.grayInputBorder};
          border-radius: 0;
          padding-left: 0;
          &:focus {
            border-bottom-color: ${colors.accent};
            box-shadow: none;
          }
        `
      default:
        return `
          background-color: ${colors.gray100};
            border: 1px solid ${colors.grayInputBorder};
          &:focus {
            background-color: ${colors.light};
            border-color: ${colors.accent};
            box-shadow: 0 0 0 0.25rem ${$secondaryWithAlpha('0.15')};
          }
        `
    }
  }}

  ${({ dimension }) => {
    switch (dimension) {
      case 'small':
        return 'font-size: 14px; min-height: 40px;'
      case 'medium':
        return 'font-size: 16px; min-height: 48px;'
      case 'big':
        return 'font-size: 16px; min-height: 56px;'
      default:
        return 'font-size: 16px; min-height: 56px;'
    }
  }}
  
  ${({ disabled }) =>
    disabled &&
    `
    background-color: ${colors.gray100};
    cursor: not-allowed;
    opacity: 0.6;
  `}
  
  &::placeholder {
    color: ${colors.gray500};
    font-size: 16px;
  }

  &:focus::placeholder {
    color: ${colors.gray400};
  }
`

const InputError = styled.span`
  font-size: 90%;
  color: ${colors.red};
  margin-top: 4px;
  display: block;
`

const TextareaInput = React.forwardRef<HTMLTextAreaElement, TextareaInputProps>(
  (
    {
      className,
      label,
      name,
      error,
      variant = 'normal',
      dimension = 'big',
      disabled = false,
      placeholder = '',
      isShowError = false,
      errorMessage = '',
      onChange,
      value,
      ...rest
    },
    ref,
  ) => {
    // Ensure value is always defined to prevent controlled/uncontrolled switching
    const normalizedValue = value ?? ''

    return (
      <div className={cn('relative w-full', className)}>
        {label && (
          <label
            htmlFor={name}
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            {label}
          </label>
        )}
        <StyledTextarea
          id={name}
          name={name}
          ref={ref}
          variant={variant}
          dimension={dimension}
          disabled={disabled}
          value={normalizedValue}
          placeholder={placeholder}
          onChange={onChange}
          aria-invalid={error ? 'true' : 'false'}
          {...rest}
        />
        {(error || (isShowError && errorMessage)) && (
          <InputError className="my-2 text-xs text-red-500">
            {error || errorMessage}
          </InputError>
        )}
      </div>
    )
  },
)

TextareaInput.displayName = 'TextareaInput'

export default TextareaInput
